import React, { useCallback } from 'react';
import { StaticContext } from 'react-router';
import { Container } from 'vendors/semantic-ui';

import { KeyValueFilter } from 'modules/shared/models';
import { Accordion, TagList, TagProps, DualSlider } from 'modules/shared-ui-advanced/components';
import { readCMSValue, getDefault } from 'modules/shared-cms/utils';
import { useRouter } from 'modules/shared/hooks';
import { DressThumbMetaProps } from '../type';

import { DressFilterListProps, DressFilterProps, SelectionFilterProps } from './dress-filter-list-type';
import { DressFilter, SelectionFilter } from './components';

import styles from './dress-filter-list-component.module.css';

const getSelectedValues = (
    filter: KeyValueFilter<keyof DressThumbMetaProps>[],
    type: keyof DressThumbMetaProps,
    isMulti?: boolean,
): string | string[] | undefined => {
    if (!filter || !type) {
        return undefined;
    }

    const currentFilter = filter.find(f => f.id === type);
    if (currentFilter && currentFilter.values) {
        if (!isMulti && currentFilter.values.length > 0) {
            return currentFilter.values[0];
        }

        return currentFilter.values;
    }

    return undefined;
};

const getSelectedFilter = (type: string, filters: DressFilterProps[], selectedValue: string): TagProps | undefined => {
    const filter = filters.find(f => readCMSValue(f.data.value) === selectedValue);
    if (filter) {
        return {
            title: getDefault(readCMSValue(filter.data.text), ''),
            value: `${type}_${getDefault(readCMSValue(filter.data.value), '')}`,
        };
    }

    return undefined;
};

const getSelectedFilters = (
    type: string,
    filters: DressFilterProps[],
    selectedValues?: string[] | string,
): TagProps[] => {
    const results: TagProps[] = [];
    if (!filters || filters.length === 0 || !selectedValues || selectedValues.length === 0) {
        return results;
    }

    if (typeof selectedValues === 'string') {
        const filter = getSelectedFilter(type, filters, selectedValues);
        if (filter) {
            results.push(filter);
        }
    } else {
        selectedValues.forEach(s => {
            const filter = getSelectedFilter(type, filters, s);
            if (filter) {
                results.push(filter);
            }
        });
    }

    return results;
};

export const DressFilterList: React.FC<DressFilterListProps> = ({
    filters,
    handleFiltersSelection,
    types,
    coupes,
    decolletes,
    longueurs,
    manches,
    selections,
    minPrice,
    maxPrice,
}: DressFilterListProps) => {
    const { history, location } = useRouter<{}, StaticContext, { activeDressFilters?: number[] }>();
    const localHandleFiltersSelection = useCallback(
        (selectedValue: string | string[], type: keyof DressThumbMetaProps): void => {
            if (!handleFiltersSelection) {
                return;
            }

            let localFilters = [...filters];
            const currentFilterIndex = localFilters.findIndex(q => q.id === type);
            if (currentFilterIndex === -1) {
                if (selectedValue) {
                    localFilters = [
                        ...localFilters,
                        { id: type, values: typeof selectedValue === 'string' ? [selectedValue] : selectedValue },
                    ];
                }
            } else {
                const currentFilter = localFilters[currentFilterIndex];
                if (!selectedValue) {
                    localFilters.splice(currentFilterIndex, 1);
                } else if (typeof selectedValue === 'string') {
                    currentFilter.values[0] = selectedValue;
                } else {
                    currentFilter.values = selectedValue;
                }
            }

            handleFiltersSelection(localFilters);
        },
        [handleFiltersSelection, filters],
    );

    const handleDeleteFilter = useCallback(
        (filterValue: string) => {
            if (!handleFiltersSelection || !filterValue) {
                return;
            }

            const localFilters = [...filters];
            const vals = filterValue.split('_');
            const currentFilter = localFilters.find(q => q.id === vals[0]);
            if (currentFilter && currentFilter.values) {
                const valueIndex = currentFilter.values.findIndex(v => v === vals[1]);
                if (valueIndex > -1) {
                    currentFilter.values.splice(valueIndex, 1);
                }
            }

            handleFiltersSelection(localFilters);
        },
        [handleFiltersSelection, filters],
    );

    const handleDeleteAllFilters = useCallback(() => {
        handleFiltersSelection([]);
    }, [handleFiltersSelection]);

    const onActiveIndexsChange = useCallback(
        activeIndexs => {
            history.replace({ ...location, state: { ...location.state, activeDressFilters: activeIndexs } });
        },
        [history, location],
    );

    const onPriceRangeChange = useCallback(
        (newMinPrice: number, newMaxPrice: number) => {
            const localFilters = [...filters];
            const currentFilter = localFilters.find(q => q.id === 'productPrice');
            if (currentFilter) {
                currentFilter.values = [`${newMinPrice}`, `${newMaxPrice}`];
            } else {
                localFilters.push({
                    id: 'productPrice',
                    values: [`${newMinPrice}`, `${newMaxPrice}`],
                });
            }

            handleFiltersSelection(localFilters);
        },
        [handleFiltersSelection, filters],
    );

    const activeDressFilters =
        location.state && location.state.activeDressFilters ? location.state.activeDressFilters : undefined;

    const filterComponentList: { title: string; component: React.ReactNode }[] = [];
    const selectedFilters: TagProps[] = [];

    if (types && types.length > 0) {
        const selectedValues = getSelectedValues(filters, 'type', true);
        selectedFilters.push(...getSelectedFilters('type', types, selectedValues));
        filterComponentList.push({
            title: 'Type',
            component: (
                <DressFilter
                    type="type"
                    values={types}
                    selectedValue={selectedValues}
                    onSelectChange={localHandleFiltersSelection}
                />
            ),
        });
    }

    if (coupes && coupes.length > 0) {
        const selectedValues = getSelectedValues(filters, 'coupes', true);
        selectedFilters.push(...getSelectedFilters('coupes', coupes, selectedValues));
        filterComponentList.push({
            title: 'Coupe',
            component: (
                <DressFilter
                    type="coupes"
                    values={coupes}
                    selectedValue={selectedValues}
                    onSelectChange={localHandleFiltersSelection}
                />
            ),
        });
    }

    if (longueurs && longueurs.length > 0) {
        const selectedValues = getSelectedValues(filters, 'longueur', true);
        selectedFilters.push(...getSelectedFilters('longueur', longueurs, selectedValues));
        filterComponentList.push({
            title: 'Longueur',
            component: (
                <DressFilter
                    type="longueur"
                    values={longueurs}
                    selectedValue={selectedValues}
                    onSelectChange={localHandleFiltersSelection}
                />
            ),
        });
    }

    if (manches && manches.length > 0) {
        const selectedValues = getSelectedValues(filters, 'manches', true);
        selectedFilters.push(...getSelectedFilters('manches', manches, selectedValues));
        filterComponentList.push({
            title: 'Manches',
            component: (
                <DressFilter
                    type="manches"
                    values={manches}
                    selectedValue={selectedValues}
                    onSelectChange={localHandleFiltersSelection}
                />
            ),
        });
    }

    if (decolletes && decolletes.length > 0) {
        const selectedValues = getSelectedValues(filters, 'decollete', true);
        selectedFilters.push(...getSelectedFilters('decollete', decolletes, selectedValues));
        filterComponentList.push({
            title: 'Décolleté',
            component: (
                <DressFilter
                    type="decollete"
                    values={decolletes}
                    selectedValue={selectedValues}
                    onSelectChange={localHandleFiltersSelection}
                />
            ),
        });
    }

    let selectionFilter: React.ReactNode;
    if (selections && selections.length > 0) {
        const selectedValues = getSelectedValues(filters, 'selections', true);
        const selectedSelections: SelectionFilterProps[] = [];
        if (selectedValues && Array.isArray(selectedValues) && selectedValues.length > 0) {
            selectedValues.forEach(v => {
                const selection = selections.find(s => s.id === v);
                if (selection) {
                    selectedSelections.push(selection);
                }
            });
        }

        selectionFilter = (
            <SelectionFilter
                type="selections"
                values={selections}
                selectedValue={selectedValues}
                onSelectChange={localHandleFiltersSelection}
            />
        );
    }

    const selectedPrices = getSelectedValues(filters, 'productPrice', true);

    return (
        <React.Fragment>
            <TagList
                tags={selectedFilters}
                onDeleteTagClick={handleDeleteFilter}
                onDeleteAllClick={handleDeleteAllFilters}
            />
            {selectionFilter && (
                <React.Fragment>
                    <Container className={styles.collectionContainer}>{selectionFilter}</Container>
                    <div className={styles.horizontalDivider} />
                </React.Fragment>
            )}
            <Accordion
                items={filterComponentList}
                activeIndexs={activeDressFilters}
                onActiveIndexsChange={onActiveIndexsChange}
                defaultOpened
            />
            <div className={styles.horizontalDivider} />
            <div className={styles.priceContainer}>
                <DualSlider
                    minValue={minPrice}
                    selectedMinValue={
                        selectedPrices && selectedPrices.length > 0 ? Number(selectedPrices[0]) : minPrice
                    }
                    maxValue={maxPrice}
                    selectedMaxValue={
                        selectedPrices && selectedPrices.length > 1 ? Number(selectedPrices[1]) : maxPrice
                    }
                    unit="€"
                    onChange={onPriceRangeChange}
                />
            </div>
        </React.Fragment>
    );
};
